import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import {
    DateTimeFilterInput,
    FloatFilterInput,
    IntFilterInput,
    KeyStringFilterInput,
    StringFilterInput
} from 'app/api/common.api.types';
import { EMPTY, from, Observable } from 'rxjs';
import { concatAll, expand, map, toArray } from 'rxjs/operators';

export interface CubeFilterInput {
    entryDate: DateTimeFilterInput;
    journal: StringFilterInput;
    account: StringFilterInput;
    auxiliary: StringFilterInput;
    entryLabel: StringFilterInput;
    debit: FloatFilterInput;
    credit: FloatFilterInput;
    lettering: StringFilterInput;
    row: IntFilterInput;
    accountLabel: StringFilterInput;
    auxiliaryLabel: StringFilterInput;
    timeline: StringFilterInput;
    gen1: StringFilterInput;
    gen2: StringFilterInput;
    gen3: StringFilterInput;
  }

export interface CubeEntry {
    companyId: string; 
    key: string;
    entryDate: string;
    journal: string | null; // Journal.Name = f(Accounting.Journal)
    originalJournal: string | null; // Accounting.Journal
    account: string;
    auxiliary: string | null;
    entryLabel: string | null;
    debit: number | null;
    credit: number | null;
    lettering: string | null;
    row: number | null;
    accountLabel: string | null; // Balance.EntryLabel = f(Accounting.Account)
    auxiliaryLabel: string | null; // Balance.EntryLabel = f(Accounting.Auxiliary)
    timeline: string | null; // Timeline
    flux: number | null; // FLUX(C-D) => Credit - Debit
    gen1: string | null; // QualifiedAccount.PCG[0].Label = f(Accounting.Account)
    gen2: string | null; // QualifiedAccount.PCG[1].Label = f(Accounting.Account)
    gen3: string | null; // QualifiedAccount.PCG[2].Label = f(Accounting.Account)
}

export interface CubeConnection {
    items: CubeEntry[];
    lastEvaluatedKey: any;
}

@Injectable({
    providedIn: 'root'
})
export class CubeAPIService {
    Cube(
        companyId: string,
        key?: KeyStringFilterInput,
        filter?: CubeFilterInput,
        limit?: number
    ): Observable<CubeEntry[]> {
        // tslint:disable-next-line: max-line-length
        const statement = `query Cube($id: String!, $key: KeyStringFilterInput, $filter: CubeFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
      company(id: $id) {
        cube(key: $key, filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            companyId
            key
            entryDate
            journal
            originalJournal
            account
            auxiliary
            entryLabel
            debit
            credit
            lettering
            row
            accountLabel
            auxiliaryLabel
            timeline
            gen1
            gen2
            gen3
          }
          lastEvaluatedKey
        }
      }
    }`;
        const gqlAPIServiceArguments: any = {};
        gqlAPIServiceArguments.id = companyId;
        if (key) {
            gqlAPIServiceArguments.key = key;
        }
        if (filter) {
            gqlAPIServiceArguments.filter = filter;
        }
        if (limit) {
            gqlAPIServiceArguments.limit = limit;
        }

        return this.performQuery(statement, gqlAPIServiceArguments).pipe(
            expand(conn => {
                const exclusiveStartKey = conn.lastEvaluatedKey;
                const args = { ...gqlAPIServiceArguments, exclusiveStartKey };
                return conn.lastEvaluatedKey &&
                    JSON.parse(conn.lastEvaluatedKey) !== ''
                    ? this.performQuery(statement, args)
                    : EMPTY;
            }),
            map(conn => conn.items),
            concatAll(),
            toArray()
        );
    }

    private performQuery(
        statement: string,
        args: any = {}
    ): Observable<CubeConnection> {
        return from(API.graphql(graphqlOperation(statement, args))).pipe(
            map((response: any): CubeConnection => {
                return response.data.company.cube;
            })
        );
    }
}

